import { toast } from "sonner";
import { Power } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useFetch } from "@/lib/hooks";
import { useAuth } from "@/context/AuthContext";
import CoinCard from "@/components/CoinCard";
import { Empty } from "@/components/common";
import { Switch } from "@/components/ui/switch";
import { LocationRow } from "@/pages/user/UserHome";
import { JobRow } from "@/pages/user/UserJobs";

function OnlineToggle({ user, refreshUser }) {
  const online = user.worker?.online;
  const toggle = async (v) => {
    try {
      await api.put("/worker/online", { online: v });
      await refreshUser();
      toast.success(v ? "Anda sekarang Online" : "Anda sekarang Offline");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span className="relative flex w-3 h-3">
          {online && <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-300 opacity-75" />}
          <span className={`relative inline-flex w-3 h-3 rounded-full ${online ? "bg-emerald-400" : "bg-slate-400"}`} />
        </span>
        <span data-testid="worker-online-status" className="font-bold">{online ? "Online" : "Offline"}</span>
        <span className="text-xs text-white/70">{online ? "· Terlihat oleh pengguna" : "· Tidak menerima pesanan"}</span>
      </div>
      <Switch data-testid="worker-online-toggle" checked={!!online} onCheckedChange={toggle} className="data-[state=checked]:bg-emerald-400" />
    </div>
  );
}

export default function WorkerHome() {
  const { user, refreshUser } = useAuth();
  const { data } = useFetch("/jobs/feed", { live: (e) => ["feed", "job", "notification"].includes(e.type) });
  return (
    <div data-testid="worker-home-section" className="space-y-6">
      <CoinCard user={user} worker>
        <div className="space-y-3">
          <OnlineToggle user={user} refreshUser={refreshUser} />
          <LocationRow user={user} onUpdated={refreshUser} />
        </div>
      </CoinCard>
      {data?.requests?.length > 0 && (
        <section>
          <h2 className="font-display font-bold text-lg mb-3">Pesanan Langsung Masuk</h2>
          <div data-testid="direct-request-list" className="space-y-3">{data.requests.map((j) => <JobRow key={j.id} j={j} />)}</div>
        </section>
      )}
      <section>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-lg">Pekerjaan sesuai keahlian</h2>
          <span className="text-xs text-slate-500">Radius &lt; 30 km</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mb-3">{(user.worker?.skills || []).map((s) => <span key={s} className="px-2.5 py-1 rounded-full bg-amber-100 text-amber-800 text-[11px] font-bold">{s}</span>)}</div>
        {!user.worker?.online && <p className="text-xs text-slate-600 bg-slate-100 rounded-xl px-3 py-2 mb-3 flex items-center gap-2"><Power className="w-3.5 h-3.5" />Aktifkan status Online agar pengguna dapat menemukan Anda.</p>}
        <div data-testid="job-feed-list" className="space-y-3">
          {!data ? <div className="h-24 rounded-2xl bg-slate-200 animate-pulse" /> :
            data.jobs.length === 0 ? <Empty title="Belum ada pekerjaan di sekitar" desc="Perbarui lokasi GPS atau tambah keahlian Anda." /> :
              data.jobs.map((j) => <div key={j.id} className="relative">{j.my_bid && <span className="absolute -top-2 right-3 z-10 text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full">Sudah ditawar</span>}<JobRow j={j} /></div>)}
        </div>
      </section>
    </div>
  );
}
